// home/handLibrary/generator.ts (CX7) - the 2-axis (gameType x pool) seed->baked-narration GENERATOR.
//
// WHY IT EXISTS. GH7 shipped a 24-entry offline narration bundle (live/narrationLibrary.ts), hand-authored
// over 8 curated seeds, so the app serves pro prose fully offline at $0. CX7 SCALES that from 24 to
// hundreds/thousands by generating the buckets deterministically instead of hand-picking seeds - the same
// offline seam (lookupNarration -> bundledNarrator), just fed by a generator.
//
// THE 2 AXES (Joe's correction 2026-07-16 - it is a 2x2, NOT a 3-way split):
//   * gameType in {cash, tournament} - the FORMAT/engine.
//   * pool in {local archetypes, pro roster skin} - WHO is in the seats.
// "Pro" is NOT a game type - it is a display/style SKIN over the same ~15 archetypes, so it overlays EITHER
// cash OR tournament. The four contexts are the 2x2 product: local-cash . pro-cash . local-tournament .
// pro-tournament. The generator therefore takes `(gameType, pool)` and NEVER branches on them internally -
// one code path builds every cell's spec, which is exactly what lets ST-LIB (local tournament, Phase 7) and
// PR-LIB (the pro column, Phase 9) slot into this same machine later without a rewrite (the seam test proves
// it by threading tournament + pro through here today).
//
// WHAT CX7 BAKES NOW vs LATER (honest sequencing, BUILD_PLAN.md §CX7):
//   * NOW: LOCAL CASH (both shipped stakes' POOLS - see the stakes-agnosticism note below). Cash exists
//     (Phase 10) and the local archetypes exist, so this cell is bakeable today.
//   * LATER via the SAME generator: local tournament (ST-LIB, gated on the Phase 7 tournament engine) and
//     the pro column across both game types (PR-LIB, gated on the Phase 9 roster skin).
//
// STAKES-AGNOSTIC BY CONSTRUCTION. `coachCacheKey` deliberately EXCLUDES dollar amounts and stakes
// (facts/cacheKey.ts: "dollar amounts ... are deliberately EXCLUDED - they differ between strategically-
// identical spots"). So the SAME strategic spot at $1/$3 and at $3/$5 (same bb-depth) buckets to the SAME
// key - the library is inherently stakes-agnostic. What actually shifts the bucket is the POOL: a different
// roster seats different archetypes, which changes the villain the digest describes and the reads that fire.
// That is why CX7 "covers both shipped stakes" by sweeping both POOLS ($1/$3 loose-passive AND $3/$5
// reg-heavy), not by re-baking the same spots with bigger numbers (which would collide on one key).
//
// COST. Enumeration (dealing hands + computing bucket keys + the coverage manifest) is PURE ENGINE and FREE -
// no LLM, no network. Only BAKING the prose costs money, and only when the narrator is the metered lane. The
// $0 `templateBakeNarrator` below bakes the deterministic template so the whole machine + the offline-play
// proof run at $0; swapping in the metered Haiku narrator is the ONE cost-gated step (BUILD_PLAN.md §CX7's
// phased gate: measure one hand -> pilot -> scale, each on Joe's typed approval).
import type {
  ActionKind,
  ArchetypeId,
  CoachText,
  FactsDigest,
  GameType,
  HandClass,
  Position,
  PositionClass,
  SprBucket,
  Stakes,
  Street,
  TableType,
  TextureClass,
  TrainingContext,
} from '@postflop/schema';
import { coachCacheKey } from '@postflop/engine';
import { digestFallbackCoachText } from '@postflop/coach';
import type { ScenarioSpec } from '@postflop/schema';
import { dealLiveHand } from '../../engine/liveRunner';
import { STAKES_POOLS } from '../stakes';
import { TOURNAMENT_CONTEXTS } from './tournamentContexts';

// ── the axes ─────────────────────────────────────────────────────────────────────────────────────────────

/** The player-pool axis. `1_3` / `3_5` / `5_10` are the shipped LOCAL cash pools (CX5 + CX8); `pro` is the
 *  roster-skin column that PR-LIB (Phase 9) fills across both game types - defined here so the generator's
 *  seam is genuinely 2-axis today, even though the pro cells are not baked until the skin lands. */
export type LibraryPoolId = '1_3' | '3_5' | '5_10' | 'pro';

/** The two orthogonal axes that pick a 2x2 cell. `gameType` is the format; `pool` is who is seated. */
export interface LibraryAxis {
  gameType: GameType;
  pool: LibraryPoolId;
}

/** A pool definition: the seat roster + table character the generator seats for a `pool`. Local pools reuse
 *  the CX5 STAKES_POOLS ground truth verbatim; `pro` is a skin (same archetype engine, `trainingContext:
 *  'pro'`) so it overlays either game type. */
export interface LibraryPoolDef {
  id: LibraryPoolId;
  label: string;
  stakes: Stakes;
  tableType: TableType;
  /** villains in seat order (primary leads); the generator rotates which one hero faces across seeds. */
  roster: ArchetypeId[];
  trainingContext: TrainingContext;
}

/** The PRO roster skin (PR-LIB column). A skin over the SAME archetype engine (North Star: "data-only
 *  villain skins over the archetype engine"), tuned to a tougher, more balanced table than the local pools.
 *  `trainingContext: 'pro'` is the only structural difference; the real-name display skin arrives with PR1. */
const POOL_PRO: LibraryPoolDef = {
  id: 'pro',
  label: 'Pro roster',
  stakes: { sb: 5, bb: 10, currency: 'USD' },
  tableType: 'aggressiveReg',
  roster: ['surgeon', 'bully', 'professor', 'volcano', 'taxCollector', 'sheriff', 'rock', 'firestarter'],
  trainingContext: 'pro',
};

/** Resolve a pool id to its definition. Local pools defer to the CX5 STAKES_POOLS source of truth so the
 *  library covers exactly the pools the app ships; `pro` is the skin above. */
export function poolDef(id: LibraryPoolId): LibraryPoolDef {
  if (id === 'pro') return POOL_PRO;
  const p = STAKES_POOLS[id];
  return {
    id,
    label: p.label,
    stakes: p.stakes,
    tableType: p.tableType,
    roster: p.roster,
    trainingContext: 'local',
  };
}

/** The four 2x2 cells, as axes. Local-cash is baked now; the other three ride the same generator later. */
export const ALL_AXES: readonly LibraryAxis[] = [
  { gameType: 'cash', pool: '1_3' },
  { gameType: 'cash', pool: '3_5' },
  { gameType: 'cash', pool: '5_10' },
  { gameType: 'cash', pool: 'pro' },
  { gameType: 'tournament', pool: '1_3' },
  { gameType: 'tournament', pool: '3_5' },
  { gameType: 'tournament', pool: '5_10' },
  { gameType: 'tournament', pool: 'pro' },
];

/** The cells CX7 actually BAKES now (cash x local pools). The rest are enumerable/routable through the same
 *  machine but are gated on Phase 7 (tournament) / Phase 9 (pro) - ST-LIB / PR-LIB own them. */
export const LOCAL_CASH_AXES: readonly LibraryAxis[] = [
  { gameType: 'cash', pool: '1_3' },
  { gameType: 'cash', pool: '3_5' },
  { gameType: 'cash', pool: '5_10' },
];

/** The LOCAL-TOURNAMENT cells ST-LIB bakes (tournament x local pools). Same machine, same generator - the
 *  ONLY difference from LOCAL_CASH_AXES is `gameType: 'tournament'`, which routes `librarySpec` through the
 *  tournament block (real ICM contexts from `tournamentContexts.ts`). The pro-tournament cell is PR-LIB. */
export const LOCAL_TOURNAMENT_AXES: readonly LibraryAxis[] = [
  { gameType: 'tournament', pool: '1_3' },
  { gameType: 'tournament', pool: '3_5' },
  { gameType: 'tournament', pool: '5_10' },
];

// ── deterministic seed -> spec (ONE code path for every cell) ───────────────────────────────────────────

/** A tiny, self-contained FNV-1a string hash -> uint32, used only to rotate roster/positions deterministically
 *  by seed (so the same seed always builds the same spec). Not security- or distribution-critical. */
function hash32(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Build the ScenarioSpec for one library hand from `(seed, axis)`. This is the WHOLE 2-axis seam: it sets
 * `gameType`, seats the pool's roster (rotated by seed so different villains lead across the sweep), and
 * carries the pool's `tableType` + `trainingContext`. It NEVER special-cases a cell - the tournament block is
 * the only additive branch, gated on `gameType === 'tournament'` exactly as ScenarioSpecSchema requires
 * (a cash spot omits it entirely). `heroPosition` is intentionally OMITTED so the generator varies hero's
 * seat across seeds (position coverage falls out for free; ScenarioSpec marks it "pin, or let the generator
 * choose").
 */
export function librarySpec(seed: string, axis: LibraryAxis): ScenarioSpec {
  const pool = poolDef(axis.pool);
  const roster = pool.roster;
  const h = hash32(`${seed}::${axis.gameType}::${axis.pool}`);
  // rotate which roster archetype hero faces (primary) + two more behind it, so villain-type + multiway
  // coverage falls out of the seed sweep.
  const primary = roster[h % roster.length];
  const second = roster[(h + 3) % roster.length];
  const third = roster[(h + 6) % roster.length];

  // ST-LIB: a tournament hand is dealt at its CONTEXT's depth (the stack ladder, deep -> bubble), so the
  // cell covers varying effective stacks. A cash hand keeps the 100bb default. TOURNAMENT_CONTEXTS is
  // engine-derived (real DT ICM), rotated by the same seed hash so villain-type AND tournament-condition
  // coverage both fall out of the sweep.
  const tourneyCtx =
    axis.gameType === 'tournament' && TOURNAMENT_CONTEXTS.length > 0
      ? TOURNAMENT_CONTEXTS[h % TOURNAMENT_CONTEXTS.length]
      : undefined;

  const spec: ScenarioSpec = {
    gameType: axis.gameType,
    tableSize: 6,
    stakes: pool.stakes,
    heroSeat: 0,
    stackDepthBB: tourneyCtx ? tourneyCtx.stackDepthBB : 100,
    villains: [
      { seat: 1, archetypeId: primary },
      { seat: 2, archetypeId: second },
      { seat: 3, archetypeId: third },
    ],
    tableType: pool.tableType,
    trainingContext: pool.trainingContext,
    seed,
  };

  if (tourneyCtx) {
    // A REAL tournament game-condition block (ST2 shape) sourced from a shipped DT structure. `icmPressure`
    // is engine-computed (tournamentContexts.ts -> riskProfileForStage), NOT a hand-picked label, and it is
    // what liveRunner threads into the card-free digest so this hand buckets + narrates as a tournament spot.
    spec.tournament = {
      phase: tourneyCtx.phase,
      payouts: tourneyCtx.payouts,
      stacks: tourneyCtx.stacks,
      effectiveStackBB: tourneyCtx.effectiveStackBB,
      payStructureId: tourneyCtx.presetId,
      icmPressure: tourneyCtx.icmPressure,
    };
  }
  return spec;
}

// ── enumerate: seed sweep -> distinct buckets (PURE, $0) ────────────────────────────────────────────────

/** One distinct bucket the generator produced: the runtime lookup key plus the manifest coordinates read
 *  straight off the card-free digest (no extra source of truth), plus the pool/gameType axis it came from
 *  (the pool is NOT in the digest - it is a generation input - so the generator carries it). */
export interface GenBucket {
  /** coachCacheKey(digest) - the exact key the offline bundle is indexed by. */
  key: string;
  axis: LibraryAxis;
  seed: string;
  street: Street;
  position: Position;
  positionClass: PositionClass;
  villainArchetypeId: ArchetypeId;
  textureClass: TextureClass;
  handClass: HandClass;
  sprBucket: SprBucket;
  playersInHand: number;
  recAction: ActionKind;
  /** the tournament ICM pressure this decision was played under (undefined for cash / deep chip-EV). Read
   *  straight off the digest so the coverage manifest can show the tournament cell's ICM spread. */
  icmPressure?: FactsDigest['context']['icmPressure'];
  /** the card-free digest for the decision - the narrator's only input, and the manifest's raw material. */
  digest: FactsDigest;
}

export interface EnumerateResult {
  buckets: GenBucket[];
  /** seeds that dealt a usable hand. */
  dealt: number;
  /** seeds that threw while dealing (a rare latent evaluator edge, or a game-type the engine cannot deal
   *  yet) - skipped, not fatal, so the generator is robust across all four cells. */
  threw: number;
  /** seeds that dealt but produced no hero postflop decision (a preflop fold-out) - no buckets, not an error. */
  empty: number;
}

/**
 * Deal every seed for one axis and collect the DISTINCT buckets (by coachCacheKey - two strategically-
 * identical spots collapse to one). Pure + deterministic: the same (axis, seeds) always yields the same
 * buckets, and it makes ZERO network/LLM calls. A seed that throws is skipped and counted (`threw`) so a
 * not-yet-dealable cell (tournament before Phase 7) degrades to "0 buckets", never a crash.
 */
export function enumerateAxis(axis: LibraryAxis, seeds: Iterable<string>): EnumerateResult {
  const byKey = new Map<string, GenBucket>();
  let dealt = 0;
  let threw = 0;
  let empty = 0;
  for (const seed of seeds) {
    let model;
    try {
      model = dealLiveHand(librarySpec(seed, axis), {});
    } catch {
      threw++;
      continue;
    }
    dealt++;
    if (model.snapshots.length === 0) {
      empty++;
      continue;
    }
    for (const snap of model.snapshots) {
      const key = coachCacheKey(snap.digest);
      if (byKey.has(key)) continue;
      const d = snap.digest;
      byKey.set(key, {
        key,
        axis,
        seed,
        street: d.street,
        position: d.context.position,
        positionClass: d.context.positionClass,
        villainArchetypeId: d.villain.archetypeId,
        textureClass: d.board.textureClass,
        handClass: d.hero.handClass,
        sprBucket: d.sprBucket,
        playersInHand: d.context.playersInHand,
        recAction: d.recommendation.action,
        icmPressure: d.context.icmPressure,
        digest: d,
      });
    }
  }
  return { buckets: [...byKey.values()], dealt, threw, empty };
}

/** A deterministic seed sweep: `${prefix}-0 ... ${prefix}-(count-1)`. The seeds are opaque rng keys (the user
 *  never sees them); more seeds = more distinct buckets, up to the finite spot space the engine can deal. */
export function librarySeeds(prefix: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => `${prefix}-${i}`);
}

/** Enumerate several axes and flatten. Convenience for baking the local-cash column (both pools) in one call. */
export function enumerateAxes(
  axes: readonly LibraryAxis[],
  seedsPerAxis: (axis: LibraryAxis) => Iterable<string>,
): EnumerateResult {
  const byKey = new Map<string, GenBucket>();
  let dealt = 0;
  let threw = 0;
  let empty = 0;
  for (const axis of axes) {
    const r = enumerateAxis(axis, seedsPerAxis(axis));
    dealt += r.dealt;
    threw += r.threw;
    empty += r.empty;
    // first cell to produce a key owns it (deterministic - axes iterate in order). A key shared across pools
    // is the stakes-agnosticism above: the same strategic spot, one stored answer.
    for (const b of r.buckets) if (!byKey.has(b.key)) byKey.set(b.key, b);
  }
  return { buckets: [...byKey.values()], dealt, threw, empty };
}

// ── bake: buckets -> prose bundle (the ONE cost-gated step, narrator-pluggable) ─────────────────────────

/** A narrator for baking: card-free digest in, the four CoachText slots out. Two bindings exist -
 *  `templateBakeNarrator` ($0, deterministic) and the metered Haiku lane (live/narration.ts `fetchNarrator`,
 *  cost-gated). Async so the metered lane drops in unchanged. */
export type BakeNarrator = (digest: FactsDigest) => Promise<CoachText>;

/** The $0 narrator: the engine's own deterministic template (digestFallbackCoachText). Makes NO network/LLM
 *  call, so the whole generate->bake->offline-play pipeline runs free. This is what proves the machine and
 *  the offline seam without spending a cent; the metered narrator swaps in for the (gated) quality batch. */
export const templateBakeNarrator: BakeNarrator = (digest) =>
  Promise.resolve(digestFallbackCoachText(digest));

/**
 * Bake a prose bundle for a set of buckets: `coachCacheKey -> CoachText`, exactly the shape
 * live/narrationLibrary.ts ships and lookupNarration reads. The narrator runs ONCE per distinct bucket
 * (dedup already happened in enumeration), so the metered lane is charged the minimum. Sequential by design:
 * the metered lane is rate-limited + KV-cached server-side, and a deterministic order keeps a resumable
 * batch reproducible.
 */
export async function bakeBundle(
  buckets: readonly GenBucket[],
  narrate: BakeNarrator,
): Promise<Record<string, CoachText>> {
  const out: Record<string, CoachText> = {};
  for (const b of buckets) {
    out[b.key] = await narrate(b.digest);
  }
  return out;
}
